// Resource table: one descriptor per shop object generates *.list and optional *.get / *.update.
// operations.ts builds the catalog from this table, then appends the named intents that
// stay (or are added) by hand. A breaking change to a generated operation is a new name.
import type { Capability } from './spec.ts'
import type { CapabilityByInput, OperationField, OutputField } from './operations.ts'
import { MESSAGE_TYPES } from './catalog-labels.ts'

export const ISO_DATE = '^[0-9]{4}-[0-9]{2}-[0-9]{2}$'

/** Rows one list call may return: one SQL query, so a view loads up to 500 rows without a paging loop */
export const LIST_LIMIT: OperationField = { type: 'integer', min: 1, max: 500, description: 'Rows per call: default 50, at most 500 in one query. Use a larger limit instead of calling page after page' }
export const LIST_OFFSET: OperationField = { type: 'integer', min: 0, description: 'Skip this many rows; result.count is the match total' }
export const LIST_QUERY: OperationField = { type: 'string', max: 100, description: 'Matches part of the name; empty or left out lists all' }
export const LIST_IDS: OperationField = { type: 'ids', maxItems: 200, description: 'Load these records' }
export const GET_IDS: OperationField = {
  type: 'ids',
  required: true,
  maxItems: 20,
  description: 'Record ids, at most 20. Missing ids appear in result.missing; the call still returns the rest.'
}
export const SORT_DIR: OperationField = { type: 'string', values: ['asc', 'desc'], description: 'Sort direction; default depends on the operation' }

const GET_DOC = 'POS detail document after the same secret redaction as johku-mcp. Extra POS fields may appear.'
const idField = (description = 'Record id'): OutputField => ({ type: 'integer', description })
const nameField: OutputField = { type: 'string', description: 'Name' }

/** Date presets of the order and invoice lists */
const DATE_PERIODS = ['today', 'yesterday', 'lastweek', 'lastmonth', 'lastyear', 'thisweek', 'thismonth', 'thisyear', 'all']
/** Presets a daterange filter can write besides DATE_PERIODS; its from/to then carry the dates */
const DATERANGE_PERIODS = ['overview', 'startingtoday', 'starting', 'custom']
/** The POS reservation list presets */
const RESERVATION_PERIODS = ['overview', 'startingtoday', 'startingtomorrow', 'startingthisweek', 'startingthismonth', 'starting', 'endingtoday', 'endingtomorrow', 'endingthisweek', 'endingthismonth']

/** Resource types POS offers (resource displayedtype), plus buyable stock and opening hours */
export const RESOURCE_TYPES = [
  'generic', 'accommodation', 'room', 'cottage', 'villa', 'meetingroom', 'space', 'theater', 'guide',
  'vehicle', 'bicycle', 'motorcycle', 'car', 'bus', 'boat', 'plane', 'rentalgear', 'rentalequipment',
  'buyable', 'openinghours'
]
const RESOURCE_TYPE_RE = `(${RESOURCE_TYPES.join('|')})`

const isoDate = (description: string): OperationField => ({
  type: 'string', max: 10, pattern: ISO_DATE, description
})

export interface ResourceDef {
  capability: Capability
  writeCapability?: Capability
  personal?: boolean
  level?: number
  navigate?: string
  note?: string
  list: {
    description: string
    filters?: Record<string, OperationField>
    output: Record<string, OutputField>
    sort?: string[]
    /** Filters that need more than the list capability, e.g. include flags with prices */
    capabilityByInput?: CapabilityByInput
  }
  update?: {
    description: string
    fields: Record<string, OperationField>
  }
  get?: {
    description: string
    output: Record<string, OutputField>
    input?: Record<string, OperationField>
  }
}

const id: OutputField = { type: 'integer', description: 'Record id; also the id for navigate when the resource has a target' }

export const RESOURCES: Record<string, ResourceDef> = {
  products: {
    capability: 'read:products',
    writeCapability: 'write:products',
    navigate: 'product',
    note: 'products.search stays as the original name; products.list is the paged catalog list',
    list: {
      description: 'List products by name or EAN, or load them by id. Rows are ordered by name. result.count is the match total.',
      filters: {
        archived: { type: 'boolean', description: 'true lists archived products instead of current ones' },
        stocked: { type: 'boolean', description: 'true lists only products with a stock count' },
        missing_ean: { type: 'boolean', description: 'true lists only products that can have an EAN (ean_allowed) but have none' },
        category_id: { type: 'integer', min: 1, description: 'Products linked to this category' },
        type: {
          type: 'string',
          values: ['sellable', 'reservable', 'content', 'ticket', 'valuecard', 'archived'],
          description: 'POS product family'
        }
      },
      sort: ['name', 'id', 'price'],
      output: {
        id,
        name: { type: 'string', description: 'Product name in the shop language' },
        ean: { type: 'string', description: 'EAN code; empty string when there is none' },
        ean_allowed: { type: 'boolean', description: 'Can have an EAN: only sellable products, not reservable or content' },
        price: { type: 'number', nullable: true, description: 'Base price in the shop currency; null when not set' },
        vat: { type: 'number', nullable: true, description: 'VAT percent; null when not set' },
        visible: { type: 'boolean', description: 'Shown in the online shop' },
        archived: { type: 'boolean', description: 'Archived' },
        type: {
          type: 'string',
          values: ['sellable', 'reservable', 'content', 'ticket', 'valuecard', 'archived'],
          description: 'POS product family; the host shows the POS title, not this key'
        },
        stock: { type: 'integer', nullable: true, description: 'Units available from stock resources; null when the product has no stock' },
        stock_resource_id: { type: 'integer', nullable: true, description: 'Stock resource id for stock.setCount; null unless exactly one resource holds the stock' }
      }
    },
    update: {
      description: 'Change name, VAT, price (sellable, non-calendar only), visible or ean. Ineligible ids go in ineligible. Not archived, type, pricerows or linked arrays.',
      fields: {
        name: { type: 'string', max: 200, description: 'Product name' },
        vat: { type: 'number', min: 0, max: 100, description: 'VAT percent' },
        price: { type: 'number', min: 0, max: 1000000, description: 'Base price; skipped for reservable, content and calendar products' },
        visible: { type: 'boolean', description: 'Shown in the online shop' },
        ean: { type: 'string', max: 32, pattern: '^[0-9A-Za-z-]{0,32}$', description: 'Empty string clears the code; skipped when ean_allowed is false' }
      }
    },
    get: {
      description: GET_DOC + ' Includes ownPricerow, ownRateplan, ownStarttime, sharedResource, tags and files.',
      output: {
        id,
        name: nameField,
        ownPricerow: { type: 'list', description: 'Price rows', item: { id: idField(), price: { type: 'number', nullable: true, description: 'Price' } } },
        ownRateplan: { type: 'list', description: 'Calendar rate plans', item: { id: idField(), title: { type: 'string', nullable: true, description: 'Title' } } },
        ownStarttime: { type: 'list', description: 'Start times', item: { id: idField() } },
        sharedResource: { type: 'list', description: 'Linked resources', item: { id: idField(), name: nameField } },
        sharedFile: { type: 'list', description: 'Linked files (metadata)', item: { id: idField(), filename: { type: 'string', nullable: true, description: 'Filename' } } }
      }
    }
  },
  orders: {
    capability: 'read:orders',
    personal: true,
    navigate: 'order',
    list: {
      description: 'List orders. Use type, tag_id, pos and period or from/to the way the POS order list does.',
      filters: {
        type: {
          type: 'string',
          values: ['unpaid', 'paid', 'notpaid', 'refunded', 'notprocessed', 'processed', 'paidnotprocessed', 'open', 'confirmed', 'grouped', 'withmore', 'paymentplans', 'archived'],
          description: 'POS Display filter'
        },
        tag_id: { type: 'integer', min: 1, description: 'Orders with this tag' },
        pos: { type: 'string', max: 32, description: 'Point of use id, or allpos / nopos' },
        from: isoDate('Order date from (ISO), with to'),
        to: isoDate('Order date to (ISO), with from'),
        period: {
          type: 'string',
          values: [...new Set([...DATE_PERIODS, ...DATERANGE_PERIODS])],
          description: 'POS date preset when from/to are left out. A daterange filter period can be passed as is; its from/to win'
        }
      },
      sort: ['id', 'name', 'orderdate', 'total'],
      output: {
        id,
        name: { type: 'string', description: 'Customer or POS name' },
        total: { type: 'number', description: 'Total including delivery' },
        paid: { type: 'boolean', description: 'Fully paid' },
        orderdate: { type: 'timestamp', description: 'Order date' },
        processed: { type: 'boolean', description: 'Marked processed' },
        confirmed: { type: 'boolean', description: 'Confirmed' },
        archived: { type: 'boolean', description: 'Archived' },
        tags: { type: 'string', nullable: true, description: 'Order tag titles, semicolon-separated' },
        origin: { type: 'string', nullable: true, description: 'Sales origin' }
      }
    },
    get: {
      description: GET_DOC + ' Same flags as johku-mcp get_order (items, reservations, deliveries) plus customer.',
      output: {
        id,
        name: nameField,
        total: { type: 'number', description: 'Total including delivery' },
        paid: { type: 'boolean', description: 'Fully paid' },
        ownItem: { type: 'list', description: 'Line items', item: { id: idField(), name: nameField, count: { type: 'integer', nullable: true, description: 'Quantity' } } },
        ownDelivery: { type: 'list', description: 'Deliveries', item: { id: idField() } },
        customer: { type: 'object', nullable: true, description: 'Linked customer', item: { id: idField(), name: nameField, email: { type: 'string', nullable: true, description: 'Email' } } }
      }
    }
  },
  reservations: {
    capability: 'read:reservations',
    personal: true,
    navigate: 'reservation',
    list: {
      description: 'List reservations. period defaults to overview when from/to are left out. With from/to the rows are the reservations that overlap the window, as in the POS list (a stay that started before from is included; one that started over 366 days before is not); starts_only or a starting* period keeps only those that start in it, and an ending* period matches the end instead.',
      filters: {
        period: {
          type: 'string',
          values: [...new Set([...RESERVATION_PERIODS, ...DATE_PERIODS, 'custom'])],
          description: 'POS reservation date preset, or a daterange filter period (from/to win when given). starting* match the start, ending* the end'
        },
        from: isoDate('Window start (ISO), with to'),
        to: isoDate('Window end (ISO), with from'),
        starts_only: { type: 'boolean', description: 'With from/to: only reservations that start in the range, instead of every reservation that overlaps it' },
        product_ids: { type: 'ids', maxItems: 50, description: 'Only these products' },
        resource_ids: { type: 'ids', maxItems: 50, description: 'Only reservations that have one of these resources (people, rooms, vehicles, equipment), as in the resource calendar' },
        include: {
          type: 'string',
          max: 40,
          pattern: '^(contact|order|resources|item)(,(contact|order|resources|item)){0,3}$',
          description: 'Comma-separated extra fields on each row: contact (client_email, client_phone), order (order_id, paid), resources (the booked resources), item (the sold line: item_id, item_total, item_vat, item_net, item_reservations, night_rates; needs read:orders or read:sales)'
        }
      },
      capabilityByInput: { input: 'include', list: true, capabilities: { item: ['read:orders', 'read:sales'] } },
      sort: ['start_ts', 'end_ts', 'product_name', 'client_name', 'id'],
      output: {
        id,
        start_ts: { type: 'timestamp', description: 'Start' },
        end_ts: { type: 'timestamp', description: 'End' },
        product_name: { type: 'string', description: 'Product' },
        product_id: { type: 'integer', nullable: true, description: 'Product id' },
        client_name: { type: 'string', nullable: true, description: 'Customer name' },
        client_company: { type: 'string', nullable: true, description: 'Customer company; null when none' },
        count: { type: 'integer', description: 'Participants or units' },
        checkin_ts: { type: 'timestamp', nullable: true, description: 'Check-in; null when not checked in' },
        checkout_ts: { type: 'timestamp', nullable: true, description: 'Check-out; null when not checked out' },
        client_email: { type: 'string', nullable: true, description: 'With include contact: email' },
        client_phone: { type: 'string', nullable: true, description: 'With include contact: phone' },
        order_id: { type: 'integer', nullable: true, description: 'With include order: the order' },
        paid: { type: 'boolean', nullable: true, description: 'With include order: the order is paid' },
        resources: { type: 'list', nullable: true, description: 'With include resources: booked resources; reservations.setResources moves a reservation to others', item: { id: idField(), name: nameField, type: { type: 'string', description: 'Resource display type, e.g. generic' } } },
        item_id: { type: 'integer', nullable: true, description: 'With include item: the order line the reservation was sold on; null when none' },
        item_total: { type: 'number', nullable: true, description: 'With include item: the whole line in the shop currency, VAT included, after line discounts (unit price × count × resource count). Order-level discounts and child lines are not in it. When item_reservations is over 1 the line covers them all: the tool decides how to split it' },
        item_vat: { type: 'number', nullable: true, description: 'With include item: VAT percent of the line' },
        item_net: { type: 'number', nullable: true, description: 'With include item: item_total without VAT, rounded to cents' },
        item_reservations: { type: 'integer', nullable: true, description: 'With include item: how many reservations share the line (rooms or parts of one booking)' },
        night_rates: { type: 'list', nullable: true, description: 'With include item: the nights calendar pricing stored for the line, each { date (ISO), rate: the night\'s price for one resource, VAT included, before line discounts }; null when the line has none', item: { date: { type: 'string', description: 'ISO date of the night' }, rate: { type: 'number', description: 'Price of the night for one resource' } } }
      }
    },
    get: {
      description: GET_DOC + ' Same flags as johku-mcp get_reservation (items, resources, seats, pin). Pin codes are [redacted] unless include_pins is true.',
      input: {
        include_pins: { type: 'boolean', description: 'Return live door/lock pin codes in ownPin[].code (default false)' }
      },
      output: {
        id,
        product_name: { type: 'string', description: 'Product' },
        client_name: { type: 'string', nullable: true, description: 'Customer name' },
        sharedResource: { type: 'list', description: 'Booked resources', item: { id: idField(), name: nameField } },
        sharedSeat: { type: 'list', description: 'Seats', item: { id: idField() } },
        ownPin: { type: 'list', description: 'Access pins; code is [redacted] unless include_pins', item: { id: idField(), code: { type: 'string', nullable: true, description: 'Pin code or [redacted]' } } }
      }
    }
  },
  bills: {
    capability: 'read:bills',
    personal: true,
    navigate: 'bill',
    list: {
      description: 'List invoices on invoicedate, with the same Display and POS filters as the POS invoice list.',
      filters: {
        type: {
          type: 'string',
          values: ['paid', 'notpaid', 'refunded', 'refunds', 'withmore'],
          description: 'POS Display filter'
        },
        pos: { type: 'string', max: 32, description: 'Point of use id, or allpos / nopos' },
        from: isoDate('Invoice date from (ISO), with to'),
        to: isoDate('Invoice date to (ISO), with from'),
        period: {
          type: 'string',
          values: [...new Set([...DATE_PERIODS, ...DATERANGE_PERIODS])],
          description: 'POS date preset when from/to are left out. A daterange filter period can be passed as is; its from/to win'
        }
      },
      sort: ['id', 'name', 'total', 'invoicedate', 'duedate'],
      output: {
        id,
        name: { type: 'string', description: 'Customer name' },
        total: { type: 'number', description: 'Total' },
        paid: { type: 'boolean', description: 'Fully paid' },
        refnumber: { type: 'string', nullable: true, description: 'Reference number' },
        invoicedate: { type: 'timestamp', nullable: true, description: 'Invoice date' },
        duedate: { type: 'timestamp', nullable: true, description: 'Due date' }
      }
    },
    get: {
      description: GET_DOC + ' Same flags as johku-mcp get_bill (items, reservations, details, messages, verifiedcustomer).',
      output: {
        id,
        name: nameField,
        total: { type: 'number', description: 'Total' },
        paid: { type: 'boolean', description: 'Fully paid' },
        ownItem: { type: 'list', description: 'Line items', item: { id: idField(), name: nameField } },
        ownPayment: { type: 'list', description: 'Payments; valuecard codes are redacted', item: { id: idField() } }
      }
    }
  },
  customers: {
    capability: 'read:customers',
    writeCapability: 'write:customers',
    personal: true,
    level: 7,
    navigate: 'customer',
    list: {
      description: 'List shop customers by name, city, phone or email.',
      sort: ['id', 'name', 'city', 'email'],
      output: {
        id,
        name: { type: 'string', description: 'Name' },
        city: { type: 'string', nullable: true, description: 'City' },
        phone: { type: 'string', nullable: true, description: 'Phone' },
        email: { type: 'string', nullable: true, description: 'Email' },
        address: { type: 'string', nullable: true, description: 'Street address' },
        postal: { type: 'string', nullable: true, description: 'Postal code' },
        country: { type: 'string', nullable: true, description: 'Country' }
      }
    },
    update: {
      description: 'Change customer contact fields. Not payment details, merge or Netvisor.',
      fields: {
        name: { type: 'string', max: 200, description: 'Name' },
        email: { type: 'string', max: 200, description: 'Email' },
        phone: { type: 'string', max: 40, description: 'Phone' },
        city: { type: 'string', max: 80, description: 'City' },
        address: { type: 'string', max: 200, description: 'Street address' },
        postal: { type: 'string', max: 20, description: 'Postal code' },
        country: { type: 'string', max: 80, description: 'Country' }
      }
    },
    get: {
      description: GET_DOC,
      output: {
        id,
        name: nameField,
        email: { type: 'string', nullable: true, description: 'Email' },
        firstname: { type: 'string', nullable: true, description: 'First name' },
        lastname: { type: 'string', nullable: true, description: 'Last name' }
      }
    }
  },
  resources: {
    capability: 'read:resources',
    writeCapability: 'write:resources',
    navigate: 'resource',
    list: {
      description: 'List resources and resource pools. displayedtype works like the POS resource list type filter; resourcetypes picks one or more resource types.',
      filters: {
        displayedtype: {
          type: 'string',
          values: ['reservable', 'orphaned', 'resourcepool', ...RESOURCE_TYPES],
          description: 'POS type filter: reservable is every type but buyable, orphaned is resources no product uses, resourcepool lists the pools. A resource type (buyable, openinghours, room, guide...) matches that type only'
        },
        resourcetypes: {
          type: 'string',
          max: 300,
          pattern: `^${RESOURCE_TYPE_RE}(,${RESOURCE_TYPE_RE})*$`,
          description: `Comma-separated resource types, e.g. room,cottage. Types: ${RESOURCE_TYPES.join(', ')}`
        },
        category_id: { type: 'integer', min: 1, description: 'Resources linked to products in this category' }
      },
      sort: ['name', 'displayedtype', 'capacity'],
      output: {
        id,
        name: { type: 'string', description: 'Name' },
        displayedtype: {
          type: 'string',
          values: [...RESOURCE_TYPES, 'resourcepool'],
          description: 'Resource type (resourcepool on pool rows); the host shows the POS title, not this key'
        },
        capacity: { type: 'integer', nullable: true, description: 'Capacity or stock count' },
        parent_id: { type: 'integer', nullable: true, description: 'Parent pool id when this row is a child; for datatable.tree' }
      }
    },
    update: {
      description: 'Change resource name or hidden. Not count (use stock.setCount).',
      fields: {
        name: { type: 'string', max: 200, description: 'Name' },
        hidden: { type: 'boolean', description: 'Hidden from pickers' }
      }
    },
    get: {
      description: GET_DOC + ' Same query as johku-mcp get_resource: ranges, stock, and the linked products and pools as id and name.',
      output: {
        id,
        name: nameField,
        displayedtype: { type: 'string', description: 'Resource type, as in resources.list' },
        count: { type: 'integer', nullable: true, description: 'Capacity (how many can be booked at once)' },
        capacity: { type: 'integer', description: 'Stock in hand for buyable resources; 0 for others' },
        ownRange: { type: 'list', description: 'Availability rules (resources.setAvailability)', item: { id: idField(), mode: { type: 'string', description: 'allow, allowstart, denyswitch or deny' }, datestart: { type: 'string', nullable: true, description: 'First day' }, dateend: { type: 'string', nullable: true, description: 'Last day' }, weekdays: { type: 'string', nullable: true, description: 'Weekdays 1-7, comma-separated' }, timestart: { type: 'string', nullable: true, description: 'Start time' }, timeend: { type: 'string', nullable: true, description: 'End time' } } },
        sharedProduct: { type: 'list', description: 'Linked products', item: { id: idField(), name: nameField } },
        sharedResourcepool: { type: 'list', description: 'Pools the resource belongs to', item: { id: idField(), name: nameField } }
      }
    }
  },
  categories: {
    capability: 'read:categories',
    writeCapability: 'write:categories',
    navigate: 'category',
    list: {
      description: 'List shop categories. parent_id is set for children so a datatable can use tree.',
      sort: ['name', 'id'],
      output: {
        id,
        name: { type: 'string', description: 'Name' },
        parent_id: { type: 'integer', nullable: true, description: 'Parent category id; for datatable.tree' }
      }
    },
    update: {
      description: 'Change category name or catalog, collection and search flags.',
      fields: {
        name: { type: 'string', max: 200, description: 'Name' },
        enable_catalog: { type: 'boolean', description: 'Shown in the catalog' },
        enable_collection: { type: 'boolean', description: 'Shown in collections' },
        enable_search: { type: 'boolean', description: 'Included in search' }
      }
    },
    get: {
      description: GET_DOC,
      output: { id, name: nameField, parent_id: { type: 'integer', nullable: true, description: 'Parent category' } }
    }
  },
  tags: {
    capability: 'read:tags',
    writeCapability: 'write:tags',
    list: {
      description: 'List tags. Use as select options (tag_id) on other lists.',
      filters: {
        category: {
          type: 'string',
          values: ['product', 'order', 'admin', 'customer', 'resource'],
          description: 'Tag family'
        }
      },
      sort: ['title', 'id'],
      output: {
        id,
        title: { type: 'string', description: 'Title' },
        category: { type: 'string', description: 'Tag family' }
      }
    },
    update: {
      description: 'Change a tag title and optional locale.',
      fields: {
        title: { type: 'string', max: 80, required: true, description: 'Title' },
        locale: { type: 'string', max: 8, description: 'Locale segment stored as category:::locale:::title' }
      }
    }
  },
  issues: {
    capability: 'read:issues',
    navigate: 'issue',
    list: {
      description: 'List the shop\'s integration and system issues, newest first.',
      filters: {
        query: { type: 'string', max: 100, description: 'Matches the message or type' },
        status: { type: 'integer', values: [0, 1], description: '0 open, 1 resolved; default both' }
      },
      output: {
        id,
        ts: { type: 'timestamp', description: 'When the issue was recorded' },
        type: { type: 'string', description: 'Issue type, e.g. the integration that raised it' },
        message: { type: 'string', description: 'Message, at most 2000 characters' },
        reftype: { type: 'string', nullable: true, description: 'Kind of the referenced record, e.g. order' },
        ref: { type: 'string', nullable: true, description: 'Id of the referenced record' },
        status: { type: 'integer', description: '0 open, 1 resolved' }
      }
    },
    get: {
      description: GET_DOC,
      output: {
        id,
        ts: { type: 'timestamp', description: 'When the issue was recorded' },
        type: { type: 'string', description: 'Issue type' },
        message: { type: 'string', description: 'Message' },
        status: { type: 'integer', description: '0 open, 1 resolved' }
      }
    }
  },
  discounts: {
    capability: 'read:discounts',
    writeCapability: 'write:discounts',
    navigate: 'discount',
    list: {
      description: 'List discount codes (not vouchers).',
      filters: {
        kind: {
          type: 'string',
          values: ['order', 'product', 'percent', 'amount'],
          description: 'order/product scope or percent/amount unit'
        }
      },
      sort: ['code', 'id'],
      output: {
        id,
        code: { type: 'string', description: 'Code' },
        type: { type: 'string', description: 'order or product' },
        amount: { type: 'number', description: 'Amount' },
        unit: { type: 'string', description: 'percent or amount' },
        usage: { type: 'integer', nullable: true, description: 'Times used' },
        remaining: { type: 'integer', nullable: true, description: 'Uses left; null when unlimited' },
        valid_ts: { type: 'timestamp', nullable: true, description: 'Valid until; null when open-ended' }
      }
    },
    update: {
      description: 'Change discount code, amount, unit, type, usage, remaining or valid_ts. Not product, category or billing-customer lists.',
      fields: {
        code: { type: 'string', max: 80, description: 'Code' },
        amount: { type: 'number', min: 0, max: 1000000, description: 'Amount' },
        unit: { type: 'string', values: ['percent', 'amount'], description: 'percent or amount' },
        type: { type: 'string', values: ['order', 'product'], description: 'order or product' },
        usage: { type: 'integer', min: 0, max: 1000000, description: 'Times used' },
        remaining: { type: 'integer', min: 0, max: 1000000, description: 'Uses left; omit to leave unlimited' },
        valid_ts: { type: 'integer', min: 0, description: 'Valid-until Unix seconds; 0 clears' }
      }
    },
    get: {
      description: GET_DOC,
      output: {
        id,
        code: { type: 'string', description: 'Code' },
        type: { type: 'string', description: 'order or product' },
        amount: { type: 'number', description: 'Amount' }
      }
    }
  },
  shows: {
    capability: 'read:shows',
    navigate: 'show',
    list: {
      description: 'List event showtimes in a date window. Also feeds calendar kind showtimes. Pass period or from/to; default thisweek.',
      filters: {
        from: isoDate('Window start (ISO); with to, overrides period'),
        to: isoDate('Window end (ISO); with from, overrides period'),
        period: { type: 'string', max: 40, description: 'POS date preset when from/to are left out; default thisweek' },
        product_id: { type: 'integer', min: 1, description: 'Limit to one event product' }
      },
      sort: ['start_ts', 'id'],
      output: {
        id,
        product_id: { type: 'integer', description: 'Event product id' },
        start_ts: { type: 'timestamp', description: 'Start' },
        end_ts: { type: 'timestamp', description: 'End' },
        name: { type: 'string', description: 'Show or product name' }
      }
    },
    get: {
      description: GET_DOC,
      output: {
        id,
        product_id: { type: 'integer', description: 'Event product id' },
        start_ts: { type: 'timestamp', description: 'Start' },
        end_ts: { type: 'timestamp', description: 'End' },
        name: nameField
      }
    }
  },
  forms: {
    capability: 'read:forms',
    list: {
      description: 'List shop forms.',
      sort: ['name', 'id'],
      output: { id, name: { type: 'string', description: 'Name' } }
    },
    get: {
      description: GET_DOC,
      output: { id, name: nameField }
    }
  },
  pricetiers: {
    capability: 'read:pricetiers',
    list: {
      description: 'List price tiers.',
      sort: ['name', 'id'],
      output: { id, name: { type: 'string', description: 'Name' } }
    },
    get: {
      description: GET_DOC,
      output: { id, name: nameField }
    }
  },
  pricing: {
    capability: 'read:pricetiers',
    list: {
      description: 'List ticket-type pricing sets.',
      sort: ['name', 'id'],
      output: { id, name: { type: 'string', description: 'Name' } }
    },
    get: {
      description: GET_DOC,
      output: { id, name: nameField }
    }
  },
  collections: {
    capability: 'read:collections',
    navigate: 'collection',
    list: {
      description: 'List collections.',
      sort: ['name', 'id'],
      output: {
        id,
        name: { type: 'string', description: 'Name' },
        categorycount: { type: 'integer', description: 'Number of categories' }
      }
    },
    get: {
      description: GET_DOC,
      output: { id, name: nameField }
    }
  },
  groups: {
    capability: 'read:groups',
    navigate: 'group',
    list: {
      description: 'List customer groups.',
      sort: ['name', 'id'],
      output: { id, name: { type: 'string', description: 'Name' } }
    },
    get: {
      description: GET_DOC,
      output: { id, name: nameField }
    }
  },
  users: {
    capability: 'read:users',
    personal: true,
    level: 7,
    list: {
      description: 'List shop users.',
      filters: {
        userlevel: { type: 'integer', min: 1, max: 10, description: 'Minimum user level' }
      },
      sort: ['name', 'id'],
      output: {
        id,
        name: { type: 'string', description: 'Name' },
        email: { type: 'string', nullable: true, description: 'Email' },
        userlevel: { type: 'integer', description: 'User level' }
      }
    }
  },
  content: {
    capability: 'read:content',
    writeCapability: 'write:content',
    navigate: 'content',
    list: {
      description: 'List leftover CMS pages. Empty when the shop has no CMS store.',
      filters: {
        type: { type: 'string', max: 40, description: 'Content type' }
      },
      sort: ['name', 'id'],
      output: {
        id,
        name: { type: 'string', description: 'Name' },
        type: { type: 'string', nullable: true, description: 'Content type' }
      }
    },
    update: {
      description: 'Change a content page name. Not the HTML body.',
      fields: { name: { type: 'string', max: 200, description: 'Name' } }
    },
    get: {
      description: GET_DOC,
      output: {
        id,
        name: nameField,
        type: { type: 'string', nullable: true, description: 'Content type' }
      }
    }
  },
  messages: {
    capability: 'read:messages',
    navigate: 'message',
    list: {
      description: 'List shop messages (email templates and checkout texts). name is the POS title (Payment link, Terms description). type is the catalog key for navigate and updates — do not show type in the UI.',
      sort: ['name', 'id'],
      output: {
        id,
        type: {
          type: 'string',
          values: [...MESSAGE_TYPES],
          description: 'Catalog key for get/update/navigate. POS shows name, not this key.'
        },
        name: { type: 'string', description: 'Human-readable POS title (Payment link, Terms description, …)' }
      }
    }
  },
  billingcustomers: {
    capability: 'read:customers',
    writeCapability: 'write:customers',
    personal: true,
    level: 7,
    navigate: 'billingcustomer',
    list: {
      description: 'List billing customers.',
      sort: ['name', 'id'],
      output: {
        id,
        company: { type: 'string', nullable: true, description: 'Company' },
        name: { type: 'string', description: 'Name' },
        email: { type: 'string', nullable: true, description: 'Email' }
      }
    },
    update: {
      description: 'Change billing customer company, name or email.',
      fields: {
        company: { type: 'string', max: 200, description: 'Company' },
        name: { type: 'string', max: 200, description: 'Name' },
        email: { type: 'string', max: 200, description: 'Email' }
      }
    },
    get: {
      description: GET_DOC,
      output: {
        id,
        company: { type: 'string', nullable: true, description: 'Company' },
        name: nameField,
        email: { type: 'string', nullable: true, description: 'Email' }
      }
    }
  },
  vouchers: {
    capability: 'read:discounts',
    list: {
      description: 'List gift vouchers and value cards.',
      sort: ['name', 'id'],
      output: {
        id,
        name: { type: 'string', description: 'Name' },
        value: { type: 'number', description: 'Face value' },
        unit: { type: 'string', description: 'percent or amount' }
      }
    }
  },
  campaigns: {
    capability: 'read:campaigns',
    level: 7,
    list: {
      description: 'List published Johku campaigns visible to the shop.',
      sort: ['id', 'title'],
      output: {
        id,
        title: { type: 'string', description: 'Title' },
        start_ts: { type: 'timestamp', nullable: true, description: 'Start' },
        end_ts: { type: 'timestamp', nullable: true, description: 'End' },
        published: { type: 'boolean', description: 'Published' }
      }
    }
  },
  paymentplans: {
    capability: 'read:payments',
    list: {
      description: 'List payment plan definitions.',
      sort: ['name', 'id'],
      output: {
        id,
        name: { type: 'string', description: 'Name' }
      }
    }
  },
  subscriptions: {
    capability: 'read:payments',
    personal: true,
    list: {
      description: 'List recurring subscriptions.',
      filters: {
        status: { type: 'string', max: 40, description: 'Subscription status' }
      },
      sort: ['id'],
      output: {
        id,
        status: { type: 'string', nullable: true, description: 'Status' },
        customer_id: { type: 'integer', nullable: true, description: 'Customer id' },
        order_id: { type: 'integer', nullable: true, description: 'Origin order id' }
      }
    },
    get: {
      description: GET_DOC,
      output: {
        id,
        status: { type: 'string', nullable: true, description: 'Status' },
        customer_id: { type: 'integer', nullable: true, description: 'Customer id' },
        order_id: { type: 'integer', nullable: true, description: 'Origin order id' }
      }
    }
  },
  poses: {
    capability: 'read:poses',
    list: {
      description: 'List points of use, for order and bill pos filters.',
      sort: ['name', 'id'],
      output: { id, name: { type: 'string', description: 'Name' } }
    }
  },
  files: {
    capability: 'read:files',
    list: {
      description: 'List file metadata (filename, title). Not file bytes.',
      sort: ['filename', 'id'],
      output: {
        id,
        filename: { type: 'string', nullable: true, description: 'Filename' },
        title: { type: 'string', nullable: true, description: 'Title' }
      }
    },
    get: {
      description: GET_DOC + ' Metadata only; file bytes are not returned.',
      output: {
        id,
        filename: { type: 'string', nullable: true, description: 'Filename' },
        title: { type: 'string', nullable: true, description: 'Title' },
        description: { type: 'string', nullable: true, description: 'Description' }
      }
    }
  },
  deliverymethods: {
    capability: 'read:deliverymethods',
    list: {
      description: 'List delivery methods. Lock codes and secrets are not included.',
      sort: ['name', 'id'],
      output: { id, name: nameField }
    }
  },
  routes: {
    capability: 'read:shows',
    list: {
      description: 'List transport routes.',
      sort: ['name', 'id'],
      output: { id, name: nameField }
    }
  },
  stops: {
    capability: 'read:shows',
    list: {
      description: 'List transport stops.',
      sort: ['name', 'id'],
      output: { id, name: nameField }
    }
  },
  zones: {
    capability: 'read:shows',
    list: {
      description: 'List transport zones.',
      sort: ['name', 'id'],
      output: { id, name: nameField }
    }
  }
}

export function listOperationName (id: string): string {
  return `${id}.list`
}

export function updateOperationName (id: string): string {
  return `${id}.update`
}

export function getOperationName (id: string): string {
  return `${id}.get`
}
